import { ContentCopy } from '@mui/icons-material';
import { Button, Paper, Stack } from '@mui/material';
import { t } from 'i18next';
import { groupBy } from 'lodash';
import { BackButton } from '../../../common/components/BackButton';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { RuleCategory } from '../../../common/enums/RuleCategory';
import { Ruleset } from '../../../common/enums/Ruleset';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS } from '../../../routing/routes';
import { CardLoader } from '../components/CardLoader';
import { FuzzerTable } from '../components/FuzzerTable/FuzzerTable';
import { PresetSubtitle } from '../components/PresetSubtitle';
import { ProfileNameInput } from '../components/ProfileNameInput';
import { SaveButton } from '../components/SaveButton';
import { FuzzerProfile } from '../models/FuzzerProfile';
import { FuzzerRule } from '../models/FuzzerRule';
import { OrganizationProfilesService } from '../services/OrganizationProfilesService';
import { RulesService } from '../services/RulesService';
import { FuzzerStore } from '../store/FuzzerStore';

type Props = {
  profile: FuzzerProfile;
  title: string;
  breadcrumbName: string;
  breadcrumbPath: string;
} & ({ isEditable: true; canDuplicate?: undefined } | { isEditable?: undefined; canDuplicate: boolean });

type State = BaseState & {
  loading: boolean;
};

export class FuzzerController extends BaseController<Props, State> {
  private readonly rulesService = new RulesService();
  private readonly profilesService = new OrganizationProfilesService();
  private fuzzerStore: FuzzerStore = FuzzerStore.getInstance();

  constructor(props: Props, context: unknown) {
    super(props, context);

    const { profile, isEditable = false } = props;

    this.fuzzerStore.setStore({
      profile,
      isEditable,
    });

    this.state = { ...super.getBaseState(), loading: true };
  }

  componentDidMount() {
    super.componentDidMount();

    this.fetchData();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.rulesService.cancelAllRequests();
  }

  render() {
    const { loading } = this.state;
    const { profile, title, isEditable = false, canDuplicate } = this.props;

    return this.renderBase(
      <SecondaryNav
        title={title}
        subtitle={profile.isPreset() && !isEditable ? <PresetSubtitle /> : undefined}
        rightSection={
          isEditable ? (
            <SaveButton store={this.fuzzerStore} onClick={this.handleCreateProfile} />
          ) : canDuplicate ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ContentCopy />}
              href={UrlHelper.generatePath(PATHS.PROFILES_CREATE_TEMPLATE, { profileId: profile.getId().toString() })}
            >
              {t('duplicateProfile')}
            </Button>
          ) : undefined
        }
        getBreadcrumbs={this.getBreadcrumbs}
        loading={loading}
      >
        <Stack gap={2}>
          <BackButton />
          {loading ? (
            <CardLoader />
          ) : (
            <>
              {isEditable && (
                <Paper elevation={2} sx={{ p: 2 }}>
                  <ProfileNameInput store={this.fuzzerStore} />
                </Paper>
              )}
              <FuzzerTable />
            </>
          )}
        </Stack>
      </SecondaryNav>
    );
  }

  private async fetchData() {
    try {
      const rules = await this.rulesService.getFuzzerRules();
      const sorted = rules.sort((r1, r2) => r1.getNumber() - r2.getNumber());
      const complianceRulesArray = sorted.filter((r) => r.getRuleset() === Ruleset.COMPLIANCE);
      const vulnerabilityRulesArray = sorted.filter((r) => r.getRuleset() === Ruleset.VULNERABILITY);

      const complianceRules = new Map(Object.entries(groupBy(complianceRulesArray, (r) => r.getCategory()))) as Map<RuleCategory, FuzzerRule[]>;
      this.fuzzerStore.setComplianceRules(complianceRules);
      const vulnerabilityRules = new Map(Object.entries(groupBy(vulnerabilityRulesArray, (r) => r.getCategory()))) as Map<RuleCategory, FuzzerRule[]>;
      this.fuzzerStore.setVulnerabilityRules(vulnerabilityRules);

      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e, { invalidUrl: t('nonExistingProfile') });
    }
  }

  @bind
  private async handleCreateProfile() {
    try {
      const profileId = await this.profilesService.createFuzzerProfile(
        this.fuzzerStore.getEditableProfileName(),
        Array.from(this.fuzzerStore.getDisabledRules())
      );
      // Profile has been created, navigate to it
      this.redirect(UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: profileId.toString() }));
    } catch (e: unknown) {
      this.handleError(e);
    }
  }

  @bind
  private getBreadcrumbs() {
    const { breadcrumbName, breadcrumbPath } = this.props;
    return this.breadcrumbFactory.getProfileBreadcrumb({ label: breadcrumbName, path: breadcrumbPath });
  }
}
